import { useContext, useEffect, useState } from "react";
import { HiX } from "react-icons/hi";
import { RxHamburgerMenu } from "react-icons/rx";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import {
  FaChevronDown,
  FaChevronUp,
  FaHandHoldingUsd,
  FaStoreAlt,
  FaUserTie,
  FaUsers,
  FaUser,
  FaLock,
  FaClock,
  FaHandshake,
  FaBuilding,
  FaBriefcase,
  FaStore,
} from "react-icons/fa";

import { Modal } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import { AuthContext } from "../context";
import RoleInfoModal from "./roleModal";

const Header = () => {
  const {
    isAuthenticated,
    setIsAuthenticated,
    isAccountModalOpen,
    openAccountModal,
    closeAccountModal,
  } = useContext(AuthContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("user_website")
    ? JSON.parse(localStorage.getItem("user_website"))
    : localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;

  const isWalletUser = token?.userType === "member";

  /* --------------------------------------------------
     FETCH CATEGORIES
  -------------------------------------------------- */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Category API is not required currently
        // because filter sidebar is not actively used.
        setCategories([]);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  /* --------------------------------------------------
     BODY SCROLL
  -------------------------------------------------- */

  useEffect(() => {
    if (isFilterSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFilterSidebarOpen]);

  /* --------------------------------------------------
     SIGNUP REDIRECTS
  -------------------------------------------------- */

  useEffect(() => {
    if (location.state?.openChannelPartnerSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/channelpartner", {
          state: {},
          replace: true,
        });
      }, 500);

      return () => clearTimeout(timer);
    }

    if (location.state?.openMemberSignUp) {
      const timer = setTimeout(() => {
        navigate("/signup/member", {
          state: {},
          replace: true,
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location.state, navigate]);

  /* --------------------------------------------------
     MENU
  -------------------------------------------------- */

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setIsFilterSidebarOpen(false);
  };

  const toggleFilterSidebar = () => {
    setIsFilterSidebarOpen((prev) => !prev);
    setIsMenuOpen(false);
  };

  /* --------------------------------------------------
     CATEGORY
  -------------------------------------------------- */

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSubcategorySelect = (category, subcategory) => {
    navigate(`/category/${category?._id}`, {
      state: {
        category,
        subcategory,
        products: subcategory.products,
      },
    });

    setIsFilterSidebarOpen(false);
  };

  /* --------------------------------------------------
     ACCOUNT
  -------------------------------------------------- */

  const handleAccountClick = () => {
    openAccountModal();
  };

  /* --------------------------------------------------
     ACCOUNT TYPE ACTION
  -------------------------------------------------- */

  const handleTypeClick = (type, action) => {
    setSelectedType(type);

    const basePath = action === "login" ? "/signin" : "/signup";

    /* END USER */

    if (type === "enduser") {
      navigate(`${basePath}/${type}`);

      if (action === "login") {
        closeAccountModal();
      }

      return;
    }

    /* LOGIN */

    if (action === "login") {
      if (type === "channelpartner") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerlogin",
          "_blank",
        );
      } else if (type === "channelpartnerplus") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/partnerpluslogin",
          "_blank",
        );
      } else if (type === "corporate") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/corporatelogin",
          "_blank",
        );
      } else if (type === "director") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/directorlogin",
          "_blank",
        );
      } else if (type === "franchise") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/franchiselogin",
          "_blank",
        );
      } else if (type === "vendor") {
        window.open(
          "https://lifeonplus.in/dashboardPanel/vendorlogin",
          "_blank",
        );
      } else if (type === "member") {
        navigate(`${basePath}/${type}`);
        closeAccountModal();
      }

      return;
    }

    /* REGISTER */

    if (action === "register") {
      navigate(`${basePath}/${type}`);
      closeAccountModal();
    }
  };

  /* --------------------------------------------------
     LOGOUT
  -------------------------------------------------- */

  const handleLogout = () => {
    localStorage.removeItem("user_website");

    if (isWalletUser) {
      localStorage.removeItem("user");
    }

    sessionStorage.clear();

    setIsAuthenticated(false);

    setIsMenuOpen(false);
    setIsFilterSidebarOpen(false);

    navigate("/", {
      state: {
        from: location.pathname,
      },
    });

    window.location.reload();
  };

  /* --------------------------------------------------
     NAVIGATION ITEMS
  -------------------------------------------------- */

  const navigationItems = [
    {
      path: "/",
      label: "Home",
    },
    {
      path: "/shop",
      label: "Shop",
    },
    {
      path: "/services",
      label: "Services",
    },
    {
      path: "/contact",
      label: "Contact Us",
    },
    {
      path: "/blog",
      label: "Blogs",
    },
    {
      path: "/ai-services",
      label: "AI Services",
    },
    {
      path: "/artery-vascular-age",
      label: "Artery-Vascular Age",
    },
  ];

  /* --------------------------------------------------
     ACCOUNT TYPES
  -------------------------------------------------- */

  const accountTypes = [
    {
      title: "Member",
      icon: <FaUsers />,
      desc: "Shop products, track orders and enjoy member benefits.",
      type: "member",
      bg: "#e9f9ee",
      color: "#02BE0F",
      knowMore: true,
    },
    {
      title: "Channel Partner",
      icon: <FaHandshake />,
      desc: "For distributors and sales partners to grow business with us.",
      type: "channelpartner",
      bg: "#e7f1ff",
      color: "#0d6efd",
      knowMore: true,
    },
    {
      title: "Channel Partner Plus",
      icon: <FaUserTie />,
      desc: "Premium partners with higher commission and priority support.",
      type: "channelpartnerplus",
      bg: "#fff4e5",
      color: "#ff9800",
      knowMore: true,
    },
    {
      title: "Corporates & Industries & Professionals",
      icon: <FaBuilding />,
      desc: "For companies and professionals buying in bulk or services.",
      type: "corporate",
      bg: "#f3e8ff",
      color: "#6f42c1",
      knowMore: true,
    },
    {
      title: "Director / Super Distributor",
      icon: <FaBriefcase />,
      desc: "Manage distribution network and oversee partner operations.",
      type: "director",
      bg: "#f1f3f5",
      color: "#343a40",
      knowMore: true,
    },
    {
      title: "Vendor",
      icon: <FaStore />,
      desc: "Sell your products and manage inventory through our platform.",
      type: "vendor",
      bg: "#e6fffa",
      color: "#20c997",
      knowMore: true,
    },
    {
      title: "Franchise",
      icon: <FaStoreAlt />,
      desc: "For distributors and sales partners to grow business with us.",
      type: "franchise",
      bg: "#e7f1ff",
      color: "#0d6efd",
      knowMore: true,
    },
    {
      title: "End User",
      icon: <FaUser />,
      desc: "Purchase and use products/services directly from platform.",
      type: "enduser",
      bg: "#f8f9fa",
      color: "#6c757d",
      knowMore: true,
    },
    {
      title: "Investor",
      icon: <FaHandHoldingUsd />,
      desc: "Invest in LifeOnPlus and be part of India's digital healthcare growth.",
      type: "investor",
      bg: "#f0fdf4",
      color: "#15803d",
      knowMore: false,
    },
  ];

  /* --------------------------------------------------
     COMING SOON
  -------------------------------------------------- */

  const comingSoonTypes = [
    {
      label: "Affiliate",
      icon: <FaHandshake />,
    },
  ];

  /* --------------------------------------------------
     JSX
  -------------------------------------------------- */

  return (
    <>
      {/* ==================================================
          HEADER
      ================================================== */}

      <header className="bg-white shadow-sm z-20 sticky top-0">
        <div className="px-4 md:px-4 flex flex-col md:flex-row justify-between items-center gap-4 py-2">
          {/* LOGO + MOBILE BUTTON */}

          <div className="flex items-center justify-between w-full md:w-auto">
            <div className="flex items-center gap-2 md:gap-3">
              <img
                className="cursor-pointer h-10 md:h-12"
                src="/lifeon-new.svg.jpeg"
                alt="LifeOnPlus Logo"
                height="40"
                onClick={() =>
                  navigate("/", {
                    state: {
                      from: location.pathname,
                    },
                  })
                }
              />
            </div>

            <button
              className="md:hidden text-gray-600 focus:outline-none"
              onClick={toggleMenu}
              aria-label="Open menu"
            >
              <RxHamburgerMenu size={28} />
            </button>
          </div>

          {/* ==================================================
              DESKTOP NAVIGATION
          ================================================== */}

          <div className="hidden md:flex items-center">
            <nav className="flex items-center gap-1 text-sm font-medium text-gray-700">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`mx-2 p-2 rounded transition-all ${
                    location.pathname === item.path
                      ? "bg-green-100 text-[#35A93E] underline underline-offset-4 font-medium"
                      : "hover:text-[#35A93E] hover:bg-green-50/60"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* ==================================================
              DESKTOP ACCOUNT AREA
          ================================================== */}

          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            {isAuthenticated ? (
              <>
                {/* CART */}

                <a
                  href="/cart"
                  className="relative no-underline text-gray-700 hover:text-[#35A93E]"
                >
                  <span className="text-2xl">🛒</span>
                  <span className="ml-1">Cart</span>
                </a>

                {/* ORDERS */}

                <a
                  href="/order-history"
                  className="relative no-underline text-gray-700 hover:text-[#35A93E]"
                >
                  <span className="text-2xl">📋</span>
                  <span className="ml-1">Order</span>
                </a>

                {/* PROFILE */}

                {!isWalletUser && (
                  <a
                    href="/profile"
                    className="relative no-underline text-gray-700 hover:text-[#35A93E]"
                  >
                    <span className="text-2xl">👤</span>
                    <span className="ml-1">Profile</span>
                  </a>
                )}

                {/* LOGOUT */}

                <button
                  className="text-white text-xs md:text-sm bg-[#35A93E] hover:bg-[#2e8b36] border-none cursor-pointer px-4 py-[7px] rounded"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                <button
                  className="flex flex-row justify-center items-center w-[90px] text-white sm:w-[80px] md:w-[100px] h-[40px] sm:h-[37px] md:h-[40px] bg-[#02BE0F] rounded-[2px] px-[8px] sm:px-[10px] gap-[8px] sm:gap-[10px]"
                  onClick={handleAccountClick}
                >
                  Account
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ==================================================
          FILTER OVERLAY
      ================================================== */}

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          isFilterSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleFilterSidebar}
      />

      {/* ==================================================
          FILTER SIDEBAR
      ================================================== */}

      <div
        className={`fixed top-0 left-0 h-full w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isFilterSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between py-3 px-4 bg-[#061939] text-white">
          <h2 className="text-lg font-semibold flex-1">Filters</h2>

          <button
            className="text-white focus:outline-none"
            onClick={toggleFilterSidebar}
            aria-label="Close filters"
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto h-[calc(100%-4rem)]">
          {categories?.length > 0 ? (
            categories.map((category) => (
              <div key={category._id} className="mb-4">
                <div
                  className="flex items-center justify-between cursor-pointer py-2"
                  onClick={() => toggleCategory(category._id)}
                >
                  <span className="text-sm font-medium text-gray-800">
                    {category.categoryName || "Unnamed Category"}
                  </span>

                  {expandedCategories[category._id] ? (
                    <FaChevronUp className="text-gray-600" size={12} />
                  ) : (
                    <FaChevronDown className="text-gray-600" size={12} />
                  )}
                </div>

                {expandedCategories[category._id] && (
                  <div className="ml-4">
                    {category.subCategories?.map((subcategory) => (
                      <div
                        key={subcategory._id}
                        className="py-2 text-sm text-gray-600 hover:text-[#35A93E] cursor-pointer"
                        onClick={() =>
                          handleSubcategorySelect(category, subcategory)
                        }
                      >
                        {subcategory.subCategoryName ||
                          "Unnamed Subcategory"}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">
              No categories available.
            </p>
          )}
        </div>
      </div>

      {/* ==================================================
          MOBILE MENU
      ================================================== */}

      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } md:hidden`}
      >
        {/* CLOSE */}

        <div className="flex justify-end p-4">
          <button
            onClick={toggleMenu}
            className="text-gray-600 focus:outline-none"
            aria-label="Close menu"
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="flex flex-col p-4 space-y-5">
          {/* MOBILE NAVIGATION */}

          <div className="flex flex-col space-y-3 border-b pb-5">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMenuOpen(false);
                }}
                className={`text-left py-2 px-3 rounded transition-all ${
                  location.pathname === item.path
                    ? "bg-green-100 text-[#35A93E] underline underline-offset-4 font-medium"
                    : "text-gray-800 hover:text-[#35A93E] hover:bg-green-50/40"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* MOBILE ACCOUNT */}

          {isAuthenticated ? (
            <div className="flex flex-col space-y-4">
              <a
                href="/cart"
                className="flex items-center gap-2 text-gray-800 text-sm hover:text-[#35A93E] no-underline"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="text-xl">🛒</span>
                Cart
              </a>

              <a
                href="/order-history"
                className="flex items-center gap-2 text-gray-800 text-sm hover:text-[#35A93E] no-underline"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="text-xl">📋</span>
                Orders
              </a>

              {!isWalletUser && (
                <a
                  href="/profile"
                  className="flex items-center gap-2 text-gray-800 text-sm hover:text-[#35A93E] no-underline"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="text-xl">👤</span>
                  Profile
                </a>
              )}

              <button
                className="text-gray-800 text-sm hover:text-[#35A93E] no-underline text-left"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleAccountClick}
                className="flex flex-row justify-center items-center w-[120px] text-white sm:w-[140px] md:w-[171px] h-[40px] sm:h-[45px] md:h-[49px] bg-[#02BE0F] rounded-[2px] px-[8px] sm:px-[10px] gap-[8px] sm:gap-[10px]"
              >
                Account
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ==================================================
          ACCOUNT SELECTION MODAL
      ================================================== */}

      <Modal
        show={isAccountModalOpen}
        onHide={closeAccountModal}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose Your Account Type</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <div className="row g-4">
            {/* ACTIVE ACCOUNT TYPES */}

            {accountTypes.map((item) => (
              <div className="col-md-3" key={item.title}>
                <div
                  className="border rounded-4 p-4 h-100 d-flex flex-column shadow-sm"
                  style={{
                    transition: "0.3s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";

                    e.currentTarget.style.boxShadow =
                      "0 10px 20px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";

                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  {/* TITLE */}

                  <h5 className="mb-2 d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "35px",
                        height: "35px",
                        background: item.bg,
                        color: item.color,
                        fontSize: "15px",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </div>

                    <p
                      className="text-sm mb-0"
                      style={{
                        lineHeight: "18px",
                      }}
                    >
                      {item.title}
                    </p>
                  </h5>

                  {/* DESCRIPTION */}

                  <p className="text-xs flex-grow-1 mb-2">{item.desc}</p>

                  {/* KNOW MORE */}

                  {item.knowMore && (
                    <p
                      className="text-xs text-primary text-decoration-underline mb-2"
                      style={{
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();

                        setShowModal(true);
                        setSelectedType(item.type);
                      }}
                    >
                      Click here to know more
                    </p>
                  )}

                  {/* BUTTONS */}

                  <div className="d-flex gap-2 mt-auto">
                    {/* REGISTER */}

                    <button
                      type="button"
                      className="flex text-sm justify-center items-center w-[80px] h-[30px] bg-[#02BE0F] text-white rounded hover:bg-[#029e0c] transition duration-200 border-0"
                      onClick={() => handleTypeClick(item.type, "register")}
                    >
                      Register
                    </button>

                    {/* LOGIN */}

                    {item.type !== "investor" && (
                      <button
                        type="button"
                        className="flex text-sm justify-center items-center w-[80px] h-[30px] border border-[#02BE0F] text-[#02BE0F] bg-white rounded"
                        onClick={() => handleTypeClick(item.type, "login")}
                      >
                        Login
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* COMING SOON */}

            {comingSoonTypes.map((item) => (
              <div className="col-md-3" key={item.label}>
                <div className="border rounded-4 p-4 h-100 d-flex flex-column shadow-sm opacity-75">
                  <h5 className="mb-2 d-flex align-items-center gap-2">
                    <FaLock className="text-secondary" size={18} />

                    <span
                      style={{
                        fontSize: "24px",
                      }}
                    >
                      {item.icon}
                    </span>

                    <span>{item.label}</span>

                    <span className="badge bg-warning text-dark ms-auto d-flex align-items-center">
                      <FaClock className="me-1" size={12} />
                      Coming Soon
                    </span>
                  </h5>

                  <p className="text-muted small flex-grow-1 mb-4">
                    This account type will be available soon. Stay tuned!
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeAccountModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* ==================================================
          TOAST
      ================================================== */}

      <ToastContainer />

      {/* ==================================================
          ROLE INFORMATION MODAL
      ================================================== */}

      <RoleInfoModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        role={selectedType}
      />

      {/* ==================================================
          CUSTOM CSS
      ================================================== */}

      <style>
        {`
          .modal-90w {
            width: 90%;
            max-width: 800px;
            margin: auto;
          }

          .modal-80w {
            width: 80%;
            max-width: 500px;
            margin: auto;
          }

          @media (min-width: 768px) {
            .modal-md {
              max-width: 400px;
            }

            .modal-lg {
              max-width: 700px;
            }
          }

          .modern-input {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 16px;
            background-color: #fff;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
            outline: none;
          }

          .modern-input:hover {
            border-color: #a3a3a3;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
          }

          .modern-input:focus {
            border-color: #35A93E;
            box-shadow: 0 0 0 3px rgba(53, 169, 62, 0.2);
          }

          .modern-input:disabled,
          .modern-input[readonly] {
            background-color: #f3f4f6;
            cursor: not-allowed;
            box-shadow: none;
          }

          @media (max-width: 767px) {
            header {
              margin-left: 0;
              margin-right: 0;
            }

            .mx-5 {
              margin-left: 0;
              margin-right: 0;
            }

            .modal-dialog {
              margin: 10px;
            }

            .modal-body {
              padding: 15px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default Header;